<template>
  <div class="container">
    <header class="title-banner">
      <h1 class="gb-page-title">Browse Present Levels</h1>
      <p class="title-subheading">Quickly view present levels statements and assessments</p>
    </header>
    <div class="row browse-body">
      <div v-if="fetchingUser" class="text-center text-muted">
        <i class="fa fa-circle-o-notch fa-spin fa-fw"/> Loading...
      </div>
      <div
        class="col-md-3"
        v-if="!fetchingUser">
        <browse-present-levels-filters
          :grade-options="gradeOptions"
          :selected-grade-value.sync="selectedGradeValue"
          :subject-options="subjectOptions"
          :selected-subject-value.sync="selectedSubjectValue"
          :category-options="categoryOptions"
          :selected-category-value.sync="selectedCategoryValue"
        />
      </div>
      <div class="col-md-9" v-if="!fetchingUser">
        <div v-if="fetchingData" class="text-center text-muted">
          <i class="fa fa-circle-o-notch fa-spin fa-fw"/> Loading...
        </div>
        <browse-present-levels-results
          v-else-if="currentCategoryData && selectedLevelValue"
          :category-data="currentCategoryData"
          :statement-options="statementOptions"
          :selected-level-value.sync="selectedLevelValue"
          :selected-grade-value="selectedGradeValue"
          :selected-category-value="selectedCategoryValue"
          :selected-subject-type="selectedSubjectType"
        />
        <div v-else class='intro-text'>
          <p>To begin, select your student's grade level.</p>
          <p>Then, view present levels statements by subject and category.</p>
          <p>Edit each statement to personalize for your student.</p>
        </div>
      </div>
    </div>
    <SubmitFeedback
      type="Browse Present Levels"/>
  </div>
</template>

<script>
import BrowsePresentLevelsFilters from '@/components/search/presentlevels/BrowsePresentLevelsFilters'
import BrowsePresentLevelsResults from '@/components/search/presentlevels/BrowsePresentLevelsResults'
import RedundantRouteMixin from '@/components/RedundantRouteMixin.js';
import {
  getEnrolledGradesSet,
  getAreasOfNeed,
  getSkillBySubject,
  getStatementsBySkill
} from '@/api/presentlevels';
import { mapGetters, mapState } from 'vuex'
import { isAcademicSubject, SUBJECT_TYPES } from '@/util/academic_subjects'
import { DEFAULT_ALT_SKILL_LEVEL } from '@/api/presentlevels'
import SubmitFeedback from '@/components/SubmitFeedback.vue'

export default {
  name: 'BrowsePresentLevelsPage',
  components: { SubmitFeedback, BrowsePresentLevelsResults, BrowsePresentLevelsFilters },
  mixins: [RedundantRouteMixin],
  props: {
    query: {
      type: Object,
      default() {
        return {}
      }
    }
  },
  data() {
    return {
      fetchingData: false,
      categoriesBySubjectName: {},
      statementsByCategoryUUID: {}
    }
  },
  computed: {
    ...mapState('user', {
      fetchingUser: state => state.fetching,
    }),
    ...mapGetters('visibility', ['selVisibility', 'effectsVisibility']),
    ...mapGetters('user', ['licenseSubType', 'userContentProfileId']),
    gradeOptions() {
      return getEnrolledGradesSet()
    },
    selectedGradeValue: {
      get() {
        return (
          this.gradeOptions
            .map(gradeData => gradeData.val)
            .includes(this.query.grade)
            ? this.query.grade
            : ''
        )
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)
        newQuery.grade = value
        delete newQuery.level

        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowsePresentLevelsPage'
        })
      }
    },
    subjectOptions() {
      return getAreasOfNeed({ selBlocked: !this.selVisibility, blockedSubjects: this.effectsVisibility ? ['Behavior'] : [] })
    },
    selectedSubjectValue: {
      get() {
        return this.subjectOptions
          .map(subjectData => subjectData.value)
          .includes(this.query.subject)
          ? this.query.subject
          : ''
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)
        newQuery.subject = value
        delete newQuery.category
        delete newQuery.level

        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowsePresentLevelsPage'
        })
      }
    },
    selectedSubjectType() {
      if (!this.selectedSubjectValue) {
        return undefined
      }
      if (isAcademicSubject(this.selectedSubjectValue)) {
        return SUBJECT_TYPES.ACADEMIC
      } else if (this.statementOptions.some( statement => {
        return statement.level_label === DEFAULT_ALT_SKILL_LEVEL
      })) {
        return SUBJECT_TYPES.ALT
      }
      return SUBJECT_TYPES.OTHER
    },
    categoryOptions() {
      if (!this.categoriesBySubjectName) {
        return []
      }
      const categories = this.categoriesBySubjectName[this.selectedSubjectValue]
      if (categories && categories.length) {
        return categories.sort((a,b) => b.locked && !a.locked ? -1 : 1)
      }
      return []
    },
    selectedCategoryValue: {
      get() {
        return this.categoryOptions
          .map(categoryData => categoryData.skill_uuid)
          .includes(this.query.category)
          ? this.query.category
          : ''
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)

        if (value) newQuery.category = value
        else delete newQuery.category

        delete newQuery.level

        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowsePresentLevelsPage'
        })
      }
    },
    currentCategoryData() {
      if (!this.categoryOptions) {
        return undefined
      }
      return this.categoryOptions.find(category => category.skill_uuid === this.selectedCategoryValue)
    },
    statementOptions() {
      if (!this.statementsByCategoryUUID || !this.selectedCategoryValue) {
        return []
      }
      const statements = this.statementsByCategoryUUID[this.selectedCategoryValue]
      if (statements && statements.length) {
        return statements
      }
      return []
    },
    selectedLevelValue: {
      get() {
        return this.statementOptions
          .map(statementOption => statementOption.level_label)
          .includes(this.query.level)
          ? this.query.level
          : ''
      },
      set(value) {
        let newQuery = Object.assign({}, this.query)

        if (value) newQuery.level = value
        else delete newQuery.level

        this.rerouteIfRouteChanged(this.$route, {
          query: newQuery,
          name: 'BrowsePresentLevelsPage'
        })
      }
    },
    defaultLevel() {
      if (!this.statementOptions || !this.statementOptions.length) {
        return ''
      }
      if (this.selectedSubjectType === SUBJECT_TYPES.ALT) {
        return DEFAULT_ALT_SKILL_LEVEL
      }
      if (this.selectedSubjectType === SUBJECT_TYPES.ACADEMIC) {
        const scalarLevelOptions = this.statementOptions.map(statement => this.scalarLevelFromlevelString(statement.level_label))
        const scalarQueryLevel = this.scalarLevelFromlevelString(this.query.grade)
        const matchedGradeLevel = scalarLevelOptions.find(level => scalarQueryLevel === level)
        if (matchedGradeLevel !== undefined) {
          return this.levelStringFromScalarLevel(matchedGradeLevel)
        }
        scalarLevelOptions.sort()
        // last option is always mastery, which is the highest, so we check against second to last option
        if (scalarQueryLevel > scalarLevelOptions[scalarLevelOptions.length-2]) {
          return 'Mastery'
        }
      }
      return this.lowestOrderedLevelFromStatements(this.statementOptions)
    },
  },
  methods: {
    lowestOrderedLevelFromStatements(statements) {
      const lowestStatement = statements.reduce((lowestStatement, nextStatement) => {
        const nextStatementOrder = parseInt(nextStatement.level_order);
        if (lowestStatement === null || nextStatementOrder < parseInt(lowestStatement.level_label)) {
          return nextStatement;
        }
        return lowestStatement;
      })
      return lowestStatement.level_label
    },
    scalarLevelFromlevelString(levelString) {
      switch (levelString) {
        case 'Pre-K':
          return -1
        case 'K':
          return 0
        case 'Mastery':
          return 13
        default:
          return parseInt(levelString.replace(/st|nd|rd|th/gi, ''))
      }
    },
    levelStringFromScalarLevel(scalarLevel) {
      switch (scalarLevel) {
        case -1:
          return 'Pre-K'
        case 0:
          return 'K'
        case 1:
          return '1st'
        case 2:
          return '2nd'
        case 3:
          return '3rd'
        case 13:
          return 'Mastery'
        default:
          return `${scalarLevel}th`
      }
    },
    async fetchCategoriesForSubject(subject) {
      if (!this.categoriesBySubjectName[subject]) {
        this.fetchingData = true
        const {skills} = await getSkillBySubject(subject, this.licenseSubType, '')
        const updatedCategoriesBySubjectName = Object.assign({}, this.categoriesBySubjectName)
        updatedCategoriesBySubjectName[subject] = skills
        this.categoriesBySubjectName = updatedCategoriesBySubjectName
        this.fetchingData = false
      }
    },
    async fetchStatementsForCategory(categoryUUID) {
      if (!this.statementsByCategoryUUID[categoryUUID]) {
        this.fetchingData = true
        const {statements} = await getStatementsBySkill(categoryUUID, this.userContentProfileId, this.licenseSubType, this.selectedSubjectValue)
        const updatedStatementsByCategoryUUID = Object.assign({}, this.statementsByCategoryUUID)
        updatedStatementsByCategoryUUID[categoryUUID] = statements.sort((a, b) => parseInt(a.level_order) - parseInt(b.level_order))
        this.statementsByCategoryUUID = updatedStatementsByCategoryUUID
        this.fetchingData = false
      }
    },
    async fetchDataFromQuery() {
      if (!this.query.subject) {
        return
      }
      if (!this.categoriesBySubjectName[this.query.subject]) {
        await this.fetchCategoriesForSubject(this.query.subject)
      }
      if (!this.categoryOptions || !this.categoryOptions.length) {
        return
      }
      if (!this.query.category || this.currentCategoryData && this.currentCategoryData.locked) {
        this.selectedCategoryValue = this.categoryOptions.find(category => !category.locked)?.skill_uuid
        return
      }
      if (!this.statementsByCategoryUUID[this.query.category]) {
        await this.fetchStatementsForCategory(this.query.category)
      }
      if (!this.statementOptions || !this.statementOptions.length) {
        return
      }
      if (!this.query.level) {
        this.selectedLevelValue = this.defaultLevel
        return
      }
      this.$sendEvent('Viewed Browse Present Levels Statement')
    },
  },
  watch: {
    query() {
      this.fetchDataFromQuery()
    }
  },
  async mounted() {
    await this.fetchDataFromQuery()
    this.$sendEvent('Viewed Browse Present Levels Page')
  },
  eventContext() {
    return {
      ref: this.query.ref,
      browse_pl_subject: this.selectedSubjectValue,
      browse_pl_filter_grade: this.selectedGradeValue,
      browse_pl_filter_category: this.currentCategoryData?.skill_name,
      browse_pl_filter_category_uuid: this.selectedCategoryValue,
      browse_pl_statement_level: this.selectedLevelValue,
    }
  }
}
</script>

<style scoped>
@import "@/components/search/browsestyles.less";
.intro-text {
  padding-top: 2em;
  font-size: 21px;
}
</style>
